// Super Admin — Homepage Logos (Our Partner + Our Affiliation Partner)
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, ImagePlus, Save, Trash2, Eye, EyeOff, Link as LinkIcon, Pencil, X } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { LoadingBlock, EmptyState } from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";

const TYPES = [
  { value: "partner", label: "Our Partner" },
  { value: "affiliation_partner", label: "Our Affiliation Partner" },
];

const EMPTY = {
  name: "",
  logo_type: "partner",
  logo_url: "",
  link_url: "",
  display_order: 0,
  show_on_homepage: true,
  status: "active",
};

export function AdminHomepageLogosPage() {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("partner");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/homepage-logos");
      setRows(data.data?.logos || []);
    } catch (err) { toast.error(formatApiError(err)); setRows([]); }
  };
  useEffect(() => { load(); }, []);

  const reset = () => { setForm(EMPTY); setEditingId(null); };

  const save = async () => {
    if (!form.name.trim() || !form.logo_url.trim()) {
      return toast.error("Name and logo URL are required.");
    }
    setBusy(true);
    try {
      const payload = { ...form, display_order: parseInt(form.display_order || 0, 10) };
      if (editingId) {
        await api.put(`/admin/homepage-logos/${editingId}`, payload);
        toast.success("Logo updated");
      } else {
        await api.post("/admin/homepage-logos", payload);
        toast.success("Logo added");
      }
      reset();
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const startEdit = (l) => {
    setEditingId(l.id);
    setForm({
      name: l.name, logo_type: l.logo_type, logo_url: l.logo_url,
      link_url: l.link_url || "", display_order: l.display_order || 0,
      show_on_homepage: !!l.show_on_homepage, status: l.status || "active",
    });
    setTab(l.logo_type);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleActive = async (l) => {
    try {
      await api.patch(`/admin/homepage-logos/${l.id}`, {
        status: l.status === "active" ? "inactive" : "active",
      });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggleShow = async (l) => {
    try {
      await api.patch(`/admin/homepage-logos/${l.id}`, {
        show_on_homepage: !l.show_on_homepage,
      });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (l) => {
    if (!window.confirm(`Delete "${l.name}"?`)) return;
    try {
      await api.delete(`/admin/homepage-logos/${l.id}`);
      toast.success("Deleted");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (rows === null) {
    return <DashboardLayout title="Homepage Logos"><LoadingBlock /></DashboardLayout>;
  }

  const filtered = rows.filter((r) => r.logo_type === tab)
    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

  return (
    <DashboardLayout title="Homepage Logos" breadcrumbs={["Admin", "Master", "Homepage Logos"]}>
      <div className="space-y-6">
        {/* FORM */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="homepage-logo-form">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ImagePlus className="h-4 w-4 text-[#101876]" />
              <div className="text-sm font-black text-slate-900">
                {editingId ? "Edit logo" : "Add a homepage logo"}
              </div>
            </div>
            {editingId && (
              <button onClick={reset} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800" data-testid="homepage-logo-cancel-edit">
                <X className="h-3 w-3" /> Cancel edit
              </button>
            )}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <F label="Section">
              <select
                value={form.logo_type}
                onChange={(e) => setForm({ ...form, logo_type: e.target.value })}
                className={inputCls}
                data-testid="homepage-logo-type"
              >
                {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </F>
            <F label="Display name">
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. NABH"
                className={inputCls}
                data-testid="homepage-logo-name"
              />
            </F>
            <F label="Logo image URL" full>
              <input
                value={form.logo_url}
                onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                placeholder="https://…/logo.png"
                className={inputCls}
                data-testid="homepage-logo-url"
              />
            </F>
            <F label="Link URL (optional)">
              <input
                value={form.link_url}
                onChange={(e) => setForm({ ...form, link_url: e.target.value })}
                placeholder="https://partner.com"
                className={inputCls}
                data-testid="homepage-logo-link"
              />
            </F>
            <F label="Display order">
              <input
                type="number"
                value={form.display_order}
                onChange={(e) => setForm({ ...form, display_order: e.target.value })}
                className={inputCls}
                data-testid="homepage-logo-order"
              />
            </F>
          </div>
          <div className="mt-3 flex flex-wrap gap-4">
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.show_on_homepage}
                onChange={(e) => setForm({ ...form, show_on_homepage: e.target.checked })}
                data-testid="homepage-logo-show"
              />
              Show on homepage
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.status === "active"}
                onChange={(e) => setForm({ ...form, status: e.target.checked ? "active" : "inactive" })}
                data-testid="homepage-logo-active"
              />
              Active
            </label>
          </div>
          {form.logo_url && (
            <div className="mt-4 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4">
              <div className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Preview</div>
              <img src={form.logo_url} alt="preview" className="h-16 max-w-[220px] object-contain" data-testid="homepage-logo-preview" />
            </div>
          )}
          <div className="mt-4 flex justify-end">
            <button
              onClick={save}
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-xl bg-[#101876] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#0d1360] disabled:opacity-70"
              data-testid="homepage-logo-save"
            >
              <Save className="h-4 w-4" />
              {busy ? "Saving…" : editingId ? "Update logo" : "Add logo"}
            </button>
          </div>
        </div>

        {/* TABS */}
        <div className="flex items-center gap-2" data-testid="homepage-logo-tabs">
          {TYPES.map((t) => {
            const active = tab === t.value;
            const count = rows.filter((r) => r.logo_type === t.value).length;
            return (
              <button
                key={t.value}
                onClick={() => setTab(t.value)}
                className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-xs font-bold transition ${active ? "border-[#101876] bg-[#101876] text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}
                data-testid={`homepage-logo-tab-${t.value}`}
              >
                <Building2 className="h-3 w-3" />
                {t.label}
                <span className={`rounded-full px-1.5 text-[10px] ${active ? "bg-white/20" : "bg-slate-100 text-slate-500"}`}>{count}</span>
              </button>
            );
          })}
        </div>

        {/* LIST */}
        <div className="rounded-2xl border border-slate-200 bg-white" data-testid="homepage-logo-list">
          {filtered.length === 0 ? (
            <EmptyState message={`No logos in "${TYPES.find(t => t.value === tab)?.label}" yet.`} />
          ) : (
            <ul className="divide-y divide-slate-100">
              {filtered.map((l) => (
                <li key={l.id} className="flex flex-wrap items-center gap-4 p-4" data-testid={`homepage-logo-row-${l.id}`}>
                  <div className="flex h-16 w-32 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 p-2">
                    <img src={l.logo_url} alt={l.name} className="max-h-12 max-w-full object-contain" />
                  </div>
                  <div className="flex-1 min-w-[180px]">
                    <div className="text-sm font-bold text-slate-900">{l.name}</div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                      <span>Order: {l.display_order ?? 0}</span>
                      {l.link_url && (
                        <a href={l.link_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#101876] hover:underline">
                          <LinkIcon className="h-3 w-3" /> Link
                        </a>
                      )}
                      <span className={`rounded-full px-2 py-0.5 font-semibold ${l.status === "active" ? "bg-green-50 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                        {l.status}
                      </span>
                      <span className={`rounded-full px-2 py-0.5 font-semibold ${l.show_on_homepage ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-500"}`}>
                        {l.show_on_homepage ? "shown" : "hidden"}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <IconBtn onClick={() => toggleShow(l)} title={l.show_on_homepage ? "Hide from homepage" : "Show on homepage"} testId={`homepage-logo-toggle-show-${l.id}`}>
                      {l.show_on_homepage ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    </IconBtn>
                    <IconBtn onClick={() => toggleActive(l)} title={l.status === "active" ? "Deactivate" : "Activate"} testId={`homepage-logo-toggle-active-${l.id}`}>
                      <span className={`inline-block h-2 w-2 rounded-full ${l.status === "active" ? "bg-green-500" : "bg-slate-300"}`} />
                    </IconBtn>
                    <IconBtn onClick={() => startEdit(l)} title="Edit" testId={`homepage-logo-edit-${l.id}`}>
                      <Pencil className="h-4 w-4" />
                    </IconBtn>
                    <IconBtn onClick={() => remove(l)} title="Delete" danger testId={`homepage-logo-delete-${l.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </IconBtn>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

const inputCls =
  "w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:border-[#101876] focus:outline-none focus:ring-2 focus:ring-[#101876]/20";

function F({ label, children, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="mb-1 block text-xs font-bold text-slate-700">{label}</label>
      {children}
    </div>
  );
}

function IconBtn({ children, onClick, title, danger, testId }) {
  return (
    <button
      onClick={onClick}
      title={title}
      data-testid={testId}
      className={`rounded-lg border border-slate-200 p-2 transition hover:bg-slate-50 ${danger ? "text-red-600 hover:border-red-200 hover:bg-red-50" : "text-slate-700"}`}
    >
      {children}
    </button>
  );
}
